import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  BookOpenIcon,
  UserGroupIcon,
  AcademicCapIcon,
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  UserIcon,
  SparklesIcon,
  BuildingLibraryIcon,
  HomeModernIcon,
  BuildingOfficeIcon,
  PrinterIcon,
  Squares2X2Icon,
  ListBulletIcon,
  EllipsisVerticalIcon,
} from "@heroicons/react/24/outline";
import { FireLoader } from "../components/Loader";
import apiClient from "../api/client";
import {
  showConfirm,
  showSuccess,
  showError,
  showInfo,
  showWarning,
} from "../utils/alert";
import { useAuth } from "../context/AuthContext";
import { socket } from "../services/socket";

// =========================
// HELPER: Extract data from various API response shapes
// =========================
const extractData = (response) => {
  const data = response?.data;
  if (!data) return { items: [], total: 0, pages: 1 };
  if (Array.isArray(data)) return { items: data, total: data.length, pages: 1 };
  if (data.data && Array.isArray(data.data)) {
    return {
      items: data.data,
      total: data.pagination?.total || data.data.length,
      pages: data.pagination?.pages || 1,
    };
  }
  if (data.rows && Array.isArray(data.rows)) {
    return {
      items: data.rows,
      total: data.count || data.rows.length,
      pages: 1,
    };
  }
  const arrayProp = Object.values(data).find((val) => Array.isArray(val));
  if (arrayProp) return { items: arrayProp, total: arrayProp.length, pages: 1 };
  return { items: [], total: 0, pages: 1 };
};

// Escape values inserted into the printable PDF document.
const escapeHtml = (value) =>
  String(value || "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");

// =========================
// HELPER: Academic class sorting
// Nursery comes first, followed by Primary, JSS and SSS. Roman numerals are
// converted to numbers so names such as Class I A, Class V B and Class VI are
// ordered by their real class number instead of alphabetically.
// =========================
const LEVEL_ORDER = {
  nursery: 0,
  primary: 1,
  jss: 2,
  junior: 2,
  sss: 3,
  senior: 3,
};

const ROMAN_VALUES = {
  I: 1,
  V: 5,
  X: 10,
};

const romanToNumber = (value) => {
  const roman = String(value || "").toUpperCase();
  if (!roman || !/^[IVX]+$/.test(roman)) return null;

  let total = 0;
  let previous = 0;
  for (let index = roman.length - 1; index >= 0; index -= 1) {
    const current = ROMAN_VALUES[roman[index]] || 0;
    if (current < previous) total -= current;
    else {
      total += current;
      previous = current;
    }
  }
  return total || null;
};

const getLevelRank = (cls) => {
  const level = String(cls?.level || "").trim().toLowerCase();
  if (Object.prototype.hasOwnProperty.call(LEVEL_ORDER, level)) {
    return LEVEL_ORDER[level];
  }

  const name = String(cls?.name || "").toLowerCase();
  if (/nursery|pre[ -]?primary|kindergarten|\bkg\b/.test(name)) return 0;
  if (/\bjss\b|junior secondary/.test(name)) return 2;
  if (/\bsss\b|senior secondary/.test(name)) return 3;
  if (/\bclass\b|primary/.test(name)) return 1;
  return 99;
};

const getClassNumber = (cls) => {
  const name = String(cls?.name || "").trim().toUpperCase();

  // Prefer the number immediately following the normal level/class label.
  const labelledMatch = name.match(
    /(?:NURSERY|PRIMARY|CLASS|JSS|SSS|GRADE|YEAR)\s*[-:]?\s*(\d+|[IVX]+)/,
  );
  const raw = labelledMatch?.[1] || name.match(/\b(\d+|[IVX]+)\b/)?.[1];
  if (!raw) return Number.MAX_SAFE_INTEGER;

  if (/^\d+$/.test(raw)) return Number(raw);
  return romanToNumber(raw) ?? Number.MAX_SAFE_INTEGER;
};

const classNameCollator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: "base",
});

const sortClassesAcademically = (items) =>
  [...items].sort((a, b) => {
    const levelDifference = getLevelRank(a) - getLevelRank(b);
    if (levelDifference !== 0) return levelDifference;

    const numberDifference = getClassNumber(a) - getClassNumber(b);
    if (numberDifference !== 0) return numberDifference;

    // After the level and class number match, this naturally places sections
    // A, B, C... in order and gives deterministic ordering to other suffixes.
    return classNameCollator.compare(
      String(a?.name || ""),
      String(b?.name || ""),
    );
  });

export default function Classes() {
  const { user, planAccess } = useAuth();
  const isStarterPlan =
    String(planAccess?.plan?.name || "").trim().toLowerCase() === "starter";
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const isTeacher = user?.role === "teacher";

  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterLevel, setFilterLevel] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalClasses, setTotalClasses] = useState(0);
  const [isPrinting, setIsPrinting] = useState(false);
  const [schoolName, setSchoolName] = useState("School");
  const [deletingId, setDeletingId] = useState(null);
  const [viewMode, setViewMode] = useState(() => {
    try {
      return localStorage.getItem("classesViewMode") || "grid";
    } catch {
      return "grid";
    }
  });
  const [activeMenuId, setActiveMenuId] = useState(null);
  const actionMenuRef = useRef(null);

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    totalStudents: 0,
    withTeachers: 0,
  });

  const fetchClassesRef = useRef();

  // ─── Fetch classes with background flag ───
  const fetchClasses = useCallback(
    async (options = { background: false }) => {
      try {
        if (!options.background) setLoading(true);

        let response;
        if (isAdmin) {
          response = await apiClient.get("/classes", {
            params: {
              page: currentPage,
              limit: 25,
              status: filterStatus !== "all" ? filterStatus : undefined,
              level: filterLevel !== "all" ? filterLevel : undefined,
              search: searchTerm || undefined,
              include: "teachers",
            },
          });
        } else if (isTeacher) {
          response = await apiClient.get("/teachers/me/classes", {
            params: {
              page: currentPage,
              limit: 25,
              status: filterStatus !== "all" ? filterStatus : undefined,
              level: filterLevel !== "all" ? filterLevel : undefined,
              search: searchTerm || undefined,
            },
          });
        } else {
          response = { data: { data: [] } };
        }
        const { items, total, pages } = extractData(response);
        setClasses(sortClassesAcademically(items));
        setTotalPages(pages || 1);
        setTotalClasses(total || items.length);

        const active = items.filter(
          (c) => c.status === "active" || c.is_active,
        ).length;
        const totalStudents = items.reduce(
          (sum, c) => sum + (c.student_count || 0),
          0,
        );
        const withTeachers = items.filter(
          (c) => c.teachers && c.teachers.length > 0,
        ).length;
        setStats({ total, active, totalStudents, withTeachers });
      } catch (error) {
        if (!options.background) {
          await showError({ text: "Failed to fetch classes" });
        }
        console.error("Error fetching classes:", error);
        setClasses([]);
        setStats({ total: 0, active: 0, totalStudents: 0, withTeachers: 0 });
      } finally {
        if (!options.background) setLoading(false);
      }
    },
    [isAdmin, isTeacher, currentPage, filterStatus, filterLevel, searchTerm],
  );

  // Store fetch function in ref
  useEffect(() => {
    fetchClassesRef.current = fetchClasses;
  }, [fetchClasses]);

  // Initial load
  useEffect(() => {
    fetchClasses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filterStatus, filterLevel, searchTerm]);

  // Load the tenant's real school name for the PDF heading and watermark.
  useEffect(() => {
    let active = true;

    const fetchSchoolName = async () => {
      const nameFromAuth =
        user?.school?.name ||
        user?.school?.schoolName ||
        user?.school_name ||
        user?.schoolName;

      try {
        const [schoolProfileResponse, currentUserResponse] = await Promise.all([
          apiClient.get("/schools/profile").catch(() => null),
          apiClient.get("/users/me").catch(() => null),
        ]);
        const schoolProfileData =
          schoolProfileResponse?.data?.data ||
          schoolProfileResponse?.data?.school ||
          schoolProfileResponse?.data ||
          {};
        const currentUserData =
          currentUserResponse?.data?.data || currentUserResponse?.data || {};
        const resolvedName =
          schoolProfileData.schoolName ||
          schoolProfileData.name ||
          schoolProfileData.school?.name ||
          schoolProfileData.school?.schoolName ||
          currentUserData.school?.name ||
          currentUserData.school?.schoolName ||
          currentUserData.school_name ||
          currentUserData.schoolName ||
          currentUserData.user?.school?.name ||
          currentUserData.user?.school_name ||
          nameFromAuth;

        if (active && resolvedName) setSchoolName(resolvedName);
      } catch (error) {
        console.error("Error fetching school name:", error);
        if (active && nameFromAuth) setSchoolName(nameFromAuth);
      }
    };

    fetchSchoolName();
    return () => {
      active = false;
    };
  }, [user]);

  // ─── Socket.IO listeners ───
  useEffect(() => {
    const handleUpdate = (event) => {
      console.log("📡 Classes update received:", event);
      if (fetchClassesRef.current) {
        fetchClassesRef.current({ background: true });
      }
    };

    socket.on("class:updated", handleUpdate);
    socket.on("student:updated", handleUpdate);
    socket.on("teacher:updated", handleUpdate);

    return () => {
      socket.off("class:updated", handleUpdate);
      socket.off("student:updated", handleUpdate);
      socket.off("teacher:updated", handleUpdate);
    };
  }, []);

  // Remember the user's preferred layout and close action menus on outside click.
  useEffect(() => {
    try {
      localStorage.setItem("classesViewMode", viewMode);
    } catch {
      // The page still works when browser storage is unavailable.
    }
  }, [viewMode]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        actionMenuRef.current &&
        !actionMenuRef.current.contains(event.target)
      ) {
        setActiveMenuId(null);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") setActiveMenuId(null);
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  const handleSearch = () => {
    setCurrentPage(1);
  };

  // =========================
  // OPTIMISTIC DELETE – only for admin
  // =========================
  const handleDelete = async (cls) => {
    if (!isAdmin) return;
    const confirmed = await showConfirm({
      title: "Delete Class",
      text: `Are you sure you want to delete "${cls.name}"? This will permanently remove the class and all associated data.${cls.student_count > 0 ? ` This class has ${cls.student_count} student(s) enrolled.` : ""}`,
      confirmText: "Yes, delete",
      icon: "error",
    });
    if (!confirmed) return;

    const previousClasses = [...classes];
    setClasses(classes.filter((c) => c.id !== cls.id));
    setDeletingId(cls.id);

    setStats((prev) => ({
      total: prev.total - 1,
      active:
        cls.status === "active" || cls.is_active
          ? prev.active - 1
          : prev.active,
      totalStudents: prev.totalStudents - (cls.student_count || 0),
      withTeachers:
        cls.teachers && cls.teachers.length > 0
          ? prev.withTeachers - 1
          : prev.withTeachers,
    }));
    setTotalClasses((prev) => prev - 1);

    try {
      await apiClient.delete(`/classes/${cls.id}`);
      await showSuccess({ text: "Class deleted successfully" });
      if (classes.length === 1 && currentPage > 1) {
        setCurrentPage((prev) => prev - 1);
      }
    } catch (error) {
      setClasses(previousClasses);
      setStats((prev) => ({
        total: prev.total + 1,
        active:
          cls.status === "active" || cls.is_active
            ? prev.active + 1
            : prev.active,
        totalStudents: prev.totalStudents + (cls.student_count || 0),
        withTeachers:
          cls.teachers && cls.teachers.length > 0
            ? prev.withTeachers + 1
            : prev.withTeachers,
      }));
      setTotalClasses((prev) => prev + 1);
      await showError({ text: "Failed to delete class" });
      console.error("Error deleting class:", error);
    } finally {
      setDeletingId(null);
    }
  };

  // =========================
  // HELPERS FOR TEACHER NAMES
  // =========================
  const getTeacherNames = (cls) => {
    if (cls.teachers && cls.teachers.length > 0) {
      return cls.teachers
        .map((t) => `${t.first_name || ""} ${t.last_name || ""}`.trim())
        .filter(Boolean)
        .join(", ");
    }
    if (cls.teacher) {
      return `${cls.teacher.first_name || ""} ${cls.teacher.last_name || ""}`.trim();
    }
    return "NO TEACHER ASSIGNED";
  };

  // =========================
  // PDF – only for admin
  // =========================
  const generateClassesPrintHTML = () => {
    const safeSchoolName = escapeHtml(schoolName || "School");
    const filterInfo = [];
    if (filterStatus !== "all")
      filterInfo.push(`Status: ${filterStatus.toUpperCase()}`);
    if (filterLevel !== "all")
      filterInfo.push(`Level: ${filterLevel.toUpperCase()}`);
    if (searchTerm) filterInfo.push(`Search: "${searchTerm}"`);
    const filterText =
      filterInfo.length > 0 ? ` (Filtered: ${filterInfo.join(", ")})` : "";

    const totalPrint = classes.length;

    let tableRows = "";
    classes.forEach((cls, index) => {
      const teacherNames = getTeacherNames(cls);
      tableRows += `
        <tr>
          <td style="padding: 6px 10px; border-bottom: 1px solid #e8e8e8; text-align: center; font-size: 11px;">${index + 1}</td>
          <td style="padding: 6px 10px; border-bottom: 1px solid #e8e8e8; font-size: 11px;">${cls.name || "Unnamed"}</td>
          <td style="padding: 6px 10px; border-bottom: 1px solid #e8e8e8; text-align: center; font-size: 11px;">${cls.level?.toUpperCase() || "N/A"}</td>
          <td style="padding: 6px 10px; border-bottom: 1px solid #e8e8e8; text-align: center; font-size: 11px;">${cls.faculty || "General"}</td>
          <td style="padding: 6px 10px; border-bottom: 1px solid #e8e8e8; font-size: 11px;">${teacherNames}</td>
          <td style="padding: 6px 10px; border-bottom: 1px solid #e8e8e8; text-align: center; font-size: 11px;">${cls.student_count || 0}</td>
          <td style="padding: 6px 10px; border-bottom: 1px solid #e8e8e8; text-align: center; font-size: 11px;">
            <span style="display:inline-block;padding:2px 10px;border-radius:3px;font-size:10px;font-weight:600;${cls.status === "active" ? "background:#d4edda;color:#155724;" : cls.status === "archived" ? "background:#fff3cd;color:#856404;" : "background:#f8d7da;color:#721c24;"}">
              ${cls.status ? cls.status.toUpperCase() : "UNKNOWN"}
            </span>
          </td>
          <td style="padding: 6px 10px; border-bottom: 1px solid #e8e8e8; text-align: center; font-size: 11px;">${cls.created_at ? new Date(cls.created_at).toLocaleDateString() : "N/A"}</td>
        </tr>
      `;
    });

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${safeSchoolName} - Classes List</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: 'Times New Roman', Times, serif; 
              padding: 25px 35px; 
              color: #1a1a1a; 
              background: white;
              font-size: 11px;
            }
            @page {
              size: A4 landscape;
              margin: 10mm 12mm;
            }
            @media print {
              .no-print { display: none !important; }
              body { padding: 15px 20px; }
              th { background: #1a3a5c !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            }
            .print-header { 
              display: flex; 
              justify-content: space-between; 
              align-items: flex-start;
              border-bottom: 3px solid #1a3a5c; 
              padding-bottom: 12px; 
              margin-bottom: 15px;
            }
            .print-header-left h1 { 
              font-size: 20px; 
              color: #1a3a5c; 
              font-weight: bold;
              letter-spacing: 1px;
              margin: 0;
            }
            .print-header-left p { 
              color: #555; 
              font-size: 11px;
              margin-top: 3px;
            }
            .print-header-right {
              text-align: right;
              font-size: 10px;
              color: #666;
            }
            .print-header-right p { margin: 2px 0; }
            .print-title {
              text-align: center;
              font-size: 14px;
              font-weight: bold;
              color: #1a3a5c;
              margin-bottom: 12px;
              letter-spacing: 0.5px;
            }
            .count-badge {
              display: inline-block;
              background: #1a3a5c;
              color: white;
              padding: 1px 10px;
              border-radius: 12px;
              font-size: 10px;
              font-weight: 600;
            }
            table { 
              width: 100%; 
              border-collapse: collapse;
              margin-top: 5px;
            }
            th { 
              background: #1a3a5c; 
              color: white; 
              padding: 7px 10px; 
              text-align: left; 
              font-size: 10px;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              font-weight: 600;
            }
            th:first-child { text-align: center; }
            td { 
              padding: 6px 10px; 
              border-bottom: 1px solid #e8e8e8; 
              font-size: 11px;
              vertical-align: middle;
            }
            tr:nth-child(even) { background: #fafafa; }
            .print-footer { 
              text-align: center; 
              margin-top: 18px; 
              padding-top: 10px; 
              border-top: 1px solid #e0e0e0; 
              color: #888; 
              font-size: 9px;
            }
            .print-footer p { margin-top: 2px; }
            .print-watermark {
              position: fixed;
              bottom: 30px;
              right: 30px;
              opacity: 0.06;
              font-size: 45px;
              color: #1a3a5c;
              font-weight: bold;
              transform: rotate(-20deg);
              pointer-events: none;
            }
            .record-count {
              text-align: right;
              font-size: 10px;
              color: #888;
              margin-top: 8px;
            }
          </style>
        </head>
        <body>
          <div class="print-watermark">${safeSchoolName}</div>

          <div class="print-header">
            <div class="print-header-left">
              <h1>${safeSchoolName}</h1>
              <p>Complete Classes List</p>
            </div>
            <div class="print-header-right">
              <p><strong>Generated:</strong> ${new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</p>
              <p><strong>Time:</strong> ${new Date().toLocaleTimeString()}</p>
              <p><strong>Report ID:</strong> #${Math.random().toString(36).substring(2, 8).toUpperCase()}</p>
            </div>
          </div>

          <div class="print-title">
            CLASSES LIST ${filterText}
            <span class="count-badge" style="margin-left:10px;">${totalPrint} Classes</span>
          </div>

          <table>
            <thead>
              <tr>
                <th style="width:4%;text-align:center;">#</th>
                <th style="width:18%;">Class Name</th>
                <th style="width:10%;text-align:center;">Level</th>
                <th style="width:12%;text-align:center;">Faculty</th>
                <th style="width:20%;">Teachers</th>
                <th style="width:8%;text-align:center;">Students</th>
                <th style="width:12%;text-align:center;">Status</th>
                <th style="width:12%;text-align:center;">Created</th>
              </tr>
            </thead>
            <tbody>
              ${
                tableRows ||
                `
                <tr>
                  <td colspan="8" style="text-align:center;padding:30px;color:#999;font-size:14px;">
                    No classes found matching the current filters.
                  </td>
                </tr>
              `
              }
            </tbody>
          </table>

          <div class="record-count">
            Total Records: <strong>${totalPrint}</strong>
          </div>

          <div class="print-footer">
            <p>This is a computer-generated document. No signature required.</p>
            <p>${new Date().getFullYear()} ${safeSchoolName} - All Rights Reserved</p>
            <p style="font-size:8px;color:#bbb;margin-top:3px;">Confidential - For authorized personnel only</p>
          </div>
        </body>
      </html>
    `;
  };

  const handlePrint = () => {
    if (isStarterPlan) {
      return showWarning({
        title: "PDF Download Not Available",
        text: "PDF downloads are not included in the Starter plan. Upgrade to Basic or a higher plan to download PDFs.",
      });
    }

    if (!isAdmin) return;
    if (classes.length === 0) {
      showWarning({
        title: "No Data",
        text: "No classes to print. Please adjust your filters.",
      });
      return;
    }
    setIsPrinting(true);
    try {
      const printWindow = window.open("", "_blank", "width=1100,height=800");
      if (printWindow) {
        printWindow.document.write(generateClassesPrintHTML());
        printWindow.document.close();
        printWindow.document.title = `${schoolName || "School"} - Classes List`;
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 500);
      }
    } catch (error) {
      console.error("Print error:", error);
      showError({ text: "Failed to generate print preview" });
    } finally {
      setIsPrinting(false);
    }
  };

  // =========================
  // UI HELPERS
  // =========================
  const renderTeachers = (cls) => {
    if (cls.teachers && cls.teachers.length > 0) {
      const names = cls.teachers
        .map((t) => `${t.first_name || ""} ${t.last_name || ""}`.trim())
        .filter(Boolean);
      if (names.length <= 2) {
        return names.join(", ");
      } else {
        return `${names.slice(0, 2).join(", ")} +${names.length - 2} more`;
      }
    }
    if (cls.teacher) {
      return (
        `${cls.teacher.first_name || ""} ${cls.teacher.last_name || ""}`.trim() ||
        "Not Assigned"
      );
    }
    return "Not Assigned";
  };

  const getStatusColor = (status) => {
    const colors = {
      active: "bg-green-100 text-green-700",
      inactive: "bg-gray-100 text-gray-700",
      archived: "bg-yellow-100 text-yellow-700",
    };
    return colors[status] || "bg-gray-100 text-gray-700";
  };

  const getStatusLabel = (status) => {
    const labels = {
      active: "Active",
      inactive: "Inactive",
      archived: "Archived",
    };
    return labels[status] || status;
  };

  const getStatusIcon = (status) => {
    if (status === "active") return CheckCircleIcon;
    if (status === "archived") return ClockIcon;
    return XCircleIcon;
  };

  const getLevelBadgeColor = (level) => {
    const colors = {
      nursery: "bg-pink-100 text-pink-700",
      primary: "bg-blue-100 text-blue-700",
      jss: "bg-purple-100 text-purple-700",
      sss: "bg-indigo-100 text-indigo-700",
    };
    return colors[level] || "bg-gray-100 text-gray-700";
  };

  const getClassIcon = (level) => {
    switch (level) {
      case "nursery":
        return HomeModernIcon;
      case "primary":
        return BuildingOfficeIcon;
      case "jss":
        return BuildingLibraryIcon;
      case "sss":
        return BuildingLibraryIcon;
      default:
        return BuildingLibraryIcon;
    }
  };

  const getLevelIconColor = (level) => {
    const colors = {
      nursery: "text-pink-600",
      primary: "text-blue-600",
      jss: "text-purple-600",
      sss: "text-indigo-600",
    };
    return colors[level] || "text-gray-600";
  };

  const changeViewMode = (mode) => {
    setViewMode(mode);
    setActiveMenuId(null);
  };

  const renderActionMenu = (cls, isDeleting) => (
    <div
      className="relative"
      ref={activeMenuId === cls.id ? actionMenuRef : null}
      onClick={(event) => event.stopPropagation()}
    >
      <button
        type="button"
        onClick={() =>
          setActiveMenuId((current) => (current === cls.id ? null : cls.id))
        }
        className={`flex h-9 w-9 items-center justify-center rounded-xl border transition-all ${
          activeMenuId === cls.id
            ? "border-primary-200 bg-primary-50 text-primary-700"
            : "border-transparent text-gray-400 hover:border-gray-200 hover:bg-gray-50 hover:text-gray-700"
        }`}
        aria-label={`Open actions for ${cls.name || "class"}`}
        aria-expanded={activeMenuId === cls.id}
      >
        <EllipsisVerticalIcon className="h-5 w-5" />
      </button>

      {activeMenuId === cls.id && (
        <div className="absolute right-0 top-11 z-40 w-48 overflow-hidden rounded-xl border border-gray-100 bg-white py-1.5 shadow-xl shadow-gray-200/70">
          <button
            type="button"
            onClick={() => {
              setActiveMenuId(null);
              navigate(`/app/classes/${cls.id}`);
            }}
            className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
          >
            <EyeIcon className="h-4 w-4 text-gray-400" />
            View class
          </button>

          {isAdmin && (
            <>
              <button
                type="button"
                onClick={() => {
                  setActiveMenuId(null);
                  navigate(`/app/classes/edit/${cls.id}`);
                }}
                className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
              >
                <PencilIcon className="h-4 w-4 text-gray-400" />
                Edit class
              </button>
              <div className="my-1 border-t border-gray-100" />
              <button
                type="button"
                onClick={() => {
                  setActiveMenuId(null);
                  handleDelete(cls);
                }}
                disabled={isDeleting}
                className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm font-medium text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50"
              >
                {isDeleting ? (
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-red-500 border-t-transparent" />
                ) : (
                  <TrashIcon className="h-4 w-4" />
                )}
                Delete class
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );

  if (loading) {
    return <FireLoader fullScreen={true} size="lg" text="LOADING..." />;
  }

  const hasClasses = classes.length > 0;

  return (
    <div className="space-y-5 sm:space-y-6 px-4 sm:px-0">
      {/* ===== SIMPLE HEADER (no gradient banner) ===== */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">
            {isAdmin ? "Classes" : "My Classes"}
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {isAdmin
              ? `Manage all ${totalClasses} classes and their information`
              : "View your assigned classes"}
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          {isAdmin && hasClasses && (
            <button
              onClick={handlePrint}
              disabled={isPrinting}
              className="inline-flex items-center px-3 py-2 sm:px-4 sm:py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm disabled:opacity-50"
            >
              <PrinterIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2" />
              <span>{isPrinting ? "Preparing PDF..." : "PDF"}</span>
            </button>
          )}
          {isAdmin && (
            <button
              onClick={() => navigate("/app/classes/add")}
              className="inline-flex items-center px-3 py-2 sm:px-4 sm:py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm"
            >
              <PlusIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2" />
              <span>Add Class</span>
            </button>
          )}
        </div>
      </div>

      {/* ===== STATS CARDS (flat) ===== */}
      {hasClasses && isAdmin && (
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm font-medium text-gray-400">
                  Total
                </p>
                <p className="text-xl sm:text-3xl font-bold text-gray-800 mt-0.5">
                  {stats.total}
                </p>
              </div>
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                <BuildingLibraryIcon className="w-5 h-5 sm:w-6 sm:h-6" />
              </div>
            </div>
            <div className="mt-2 pt-2 border-t border-gray-100">
              <p className="text-[10px] sm:text-xs text-gray-400">
                All classes
              </p>
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm font-medium text-gray-400">
                  Active
                </p>
                <p className="text-xl sm:text-3xl font-bold text-green-600 mt-0.5">
                  {stats.active}
                </p>
              </div>
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-green-50 text-green-600 flex items-center justify-center">
                <CheckCircleIcon className="w-5 h-5 sm:w-6 sm:h-6" />
              </div>
            </div>
            <div className="mt-2 pt-2 border-t border-gray-100">
              <p className="text-[10px] sm:text-xs text-gray-400">
                {stats.total > 0
                  ? Math.round((stats.active / stats.total) * 100)
                  : 0}
                % of total
              </p>
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm font-medium text-gray-400">
                  Students
                </p>
                <p className="text-xl sm:text-3xl font-bold text-purple-600 mt-0.5">
                  {stats.totalStudents}
                </p>
              </div>
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center">
                <UserGroupIcon className="w-5 h-5 sm:w-6 sm:h-6" />
              </div>
            </div>
            <div className="mt-2 pt-2 border-t border-gray-100">
              <p className="text-[10px] sm:text-xs text-gray-400">
                Total enrolled
              </p>
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm font-medium text-gray-400">
                  With Teachers
                </p>
                <p className="text-xl sm:text-3xl font-bold text-orange-600 mt-0.5">
                  {stats.withTeachers}
                </p>
              </div>
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-orange-50 text-orange-600 flex items-center justify-center">
                <UserIcon className="w-5 h-5 sm:w-6 sm:h-6" />
              </div>
            </div>
            <div className="mt-2 pt-2 border-t border-gray-100">
              <p className="text-[10px] sm:text-xs text-gray-400">
                {stats.total > 0
                  ? Math.round((stats.withTeachers / stats.total) * 100)
                  : 0}
                % have teachers
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ===== SEARCH & FILTER (flat) ===== */}
      {isAdmin && hasClasses && (
        <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4">
          <div className="flex flex-col md:flex-row gap-3 sm:gap-4">
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search classes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-2.5 rounded-lg border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none bg-gray-50 text-gray-700 placeholder-gray-400 text-sm transition-colors"
              />
            </div>
            <div className="flex flex-wrap gap-2 sm:gap-3">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="flex-1 min-w-[100px] px-3 py-2 sm:px-4 sm:py-2.5 rounded-lg border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none bg-gray-50 text-gray-700 text-sm transition-colors"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="archived">Archived</option>
              </select>
              <select
                value={filterLevel}
                onChange={(e) => setFilterLevel(e.target.value)}
                className="flex-1 min-w-[100px] px-3 py-2 sm:px-4 sm:py-2.5 rounded-lg border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none bg-gray-50 text-gray-700 text-sm transition-colors"
              >
                <option value="all">All Levels</option>
                <option value="nursery">Nursery</option>
                <option value="primary">Primary</option>
                <option value="jss">JSS</option>
                <option value="sss">SSS</option>
              </select>
              <button
                onClick={handleSearch}
                className="flex-1 px-4 py-2 sm:px-4 sm:py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm"
              >
                Search
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== RESULT BAR & VIEW SWITCHER ===== */}
      {hasClasses && (
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-gray-700">
              {totalClasses} {totalClasses === 1 ? "class" : "classes"}
            </p>
            <p className="hidden text-xs text-gray-400 sm:block">
              Choose how you want to view your classes
            </p>
          </div>
          <div
            className="inline-flex items-center rounded-xl border border-gray-200 bg-white p-1 shadow-sm"
            aria-label="Choose classes layout"
          >
            <button
              type="button"
              onClick={() => changeViewMode("grid")}
              className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-all ${
                viewMode === "grid"
                  ? "bg-primary-600 text-white shadow-sm"
                  : "text-gray-500 hover:bg-gray-50 hover:text-gray-800"
              }`}
              title="Card view"
            >
              <Squares2X2Icon className="h-4 w-4" />
              <span className="hidden sm:inline">Cards</span>
            </button>
            <button
              type="button"
              onClick={() => changeViewMode("list")}
              className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-all ${
                viewMode === "list"
                  ? "bg-primary-600 text-white shadow-sm"
                  : "text-gray-500 hover:bg-gray-50 hover:text-gray-800"
              }`}
              title="List view"
            >
              <ListBulletIcon className="h-4 w-4" />
              <span className="hidden sm:inline">List</span>
            </button>
          </div>
        </div>
      )}

      {/* ===== CLASSES – CARD OR LIST VIEW ===== */}
      {!hasClasses ? (
        <div className="bg-white rounded-lg border border-gray-200 p-6 sm:p-12 text-center">
          <div className="flex flex-col items-center justify-center space-y-4 sm:space-y-6">
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gray-100 flex items-center justify-center">
              <BuildingLibraryIcon className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400" />
            </div>
            <div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-800">
                {isAdmin
                  ? "No Classes Yet"
                  : "You are not assigned to any class"}
              </h3>
              <p className="text-sm sm:text-base text-gray-500 mt-1 sm:mt-2 max-w-md mx-auto">
                {isAdmin
                  ? "Get started by creating your first class. Classes help organize students and teachers effectively."
                  : "Please contact the administrator to assign you to a class."}
              </p>
            </div>
            {isAdmin && (
              <div className="flex flex-col sm:flex-row gap-3 mt-2 sm:mt-4">
                <button
                  onClick={() => navigate("/app/classes/add")}
                  className="inline-flex items-center justify-center px-5 py-2.5 sm:px-6 sm:py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm"
                >
                  <PlusIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                  Add Your First Class
                </button>
                <button
                  onClick={() =>
                    showInfo({
                      title: "How to Add Classes",
                      text: 'You can add classes like Nursery 1, Primary 1, JSS 1, etc. Use the "Add Class" button.',
                    })
                  }
                  className="inline-flex items-center justify-center px-5 py-2.5 sm:px-6 sm:py-3 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                >
                  <SparklesIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                  Learn How
                </button>
              </div>
            )}
          </div>
        </div>
      ) : viewMode === "grid" ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 xl:grid-cols-3">
          {classes.map((cls) => {
            const StatusIcon = getStatusIcon(cls.status);
            const ClassIcon = getClassIcon(cls.level);
            const iconColor = getLevelIconColor(cls.level);
            const teacherDisplay = renderTeachers(cls);
            const isDeleting = deletingId === cls.id;

            return (
              <div
                key={cls.id}
                className={`group relative cursor-pointer overflow-visible rounded-2xl border border-gray-200 bg-white transition-all duration-200 hover:-translate-y-0.5 hover:border-primary-200 hover:shadow-lg hover:shadow-gray-200/60 ${isDeleting ? "pointer-events-none opacity-50" : ""}`}
                onClick={() => navigate(`/app/classes/${cls.id}`)}
              >
                <div className="h-1.5 rounded-t-2xl bg-gradient-to-r from-primary-500 to-primary-300" />
                <div className="p-4 sm:p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-gray-50 ring-1 ring-gray-100 transition-colors group-hover:bg-primary-50 group-hover:ring-primary-100 sm:h-14 sm:w-14">
                          <ClassIcon
                            className={`w-6 h-6 sm:w-7 sm:h-7 ${iconColor}`}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-800 text-base sm:text-lg group-hover:text-primary-700 transition-colors truncate">
                            {cls.name || "Unnamed Class"}
                          </h3>
                          <div className="flex flex-wrap items-center gap-1.5 mt-1">
                            <span
                              className={`text-[10px] sm:text-xs px-1.5 py-0.5 sm:px-2 sm:py-0.5 rounded-full ${getLevelBadgeColor(cls.level)}`}
                            >
                              {cls.level?.toUpperCase() || "N/A"}
                            </span>
                            <span className="text-[10px] sm:text-xs text-gray-400">
                              {cls.faculty || "General"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    {renderActionMenu(cls, isDeleting)}
                  </div>

                  <div className="mt-3 sm:mt-4 grid grid-cols-2 gap-2 sm:gap-3">
                    <div className="bg-gray-50 rounded-lg p-2 sm:p-3 text-center group-hover:bg-gray-100 transition-colors">
                      <p className="text-[10px] sm:text-sm text-gray-400">
                        Students
                      </p>
                      <p className="text-base sm:text-xl font-bold text-gray-800">
                        {cls.student_count || 0}
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-2 sm:p-3 text-center group-hover:bg-gray-100 transition-colors">
                      <p className="text-[10px] sm:text-sm text-gray-400">
                        Teachers
                      </p>
                      <p
                        className="text-xs sm:text-sm font-medium text-gray-700 truncate"
                        title={teacherDisplay}
                      >
                        {teacherDisplay}
                      </p>
                    </div>
                  </div>

                  <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-100 flex flex-wrap items-center justify-between gap-2">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 sm:px-3 sm:py-1 text-[10px] sm:text-xs font-medium rounded-full ${getStatusColor(cls.status)}`}
                    >
                      <StatusIcon className="w-3 h-3 mr-1" />
                      {getStatusLabel(cls.status)}
                    </span>
                    <span className="text-[10px] sm:text-sm text-gray-400">
                      {cls.created_at
                        ? new Date(cls.created_at).toLocaleDateString()
                        : "N/A"}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="space-y-3">
          {classes.map((cls) => {
            const StatusIcon = getStatusIcon(cls.status);
            const ClassIcon = getClassIcon(cls.level);
            const iconColor = getLevelIconColor(cls.level);
            const teacherDisplay = renderTeachers(cls);
            const isDeleting = deletingId === cls.id;

            return (
              <div
                key={cls.id}
                onClick={() => navigate(`/app/classes/${cls.id}`)}
                className={`group cursor-pointer rounded-2xl border border-gray-200 bg-white p-4 transition-all duration-200 hover:border-primary-200 hover:shadow-md hover:shadow-gray-200/50 sm:p-5 ${isDeleting ? "pointer-events-none opacity-50" : ""}`}
              >
                <div className="flex items-start gap-3 sm:items-center sm:gap-4">
                  <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-gray-50 ring-1 ring-gray-100 transition-colors group-hover:bg-primary-50 sm:h-12 sm:w-12">
                    <ClassIcon
                      className={`h-5 w-5 sm:h-6 sm:w-6 ${iconColor}`}
                    />
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="truncate text-sm font-semibold text-gray-900 transition-colors group-hover:text-primary-700 sm:text-base">
                        {cls.name || "Unnamed Class"}
                      </h3>
                      <span
                        className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${getLevelBadgeColor(cls.level)}`}
                      >
                        {cls.level?.toUpperCase() || "N/A"}
                      </span>
                    </div>
                    <p className="mt-1 truncate text-xs text-gray-400 sm:text-sm">
                      {cls.faculty || "General"} faculty
                    </p>
                  </div>

                  <div className="hidden min-w-[95px] sm:block">
                    <p className="text-xs text-gray-400">Students</p>
                    <p className="mt-0.5 text-sm font-semibold text-gray-800">
                      {cls.student_count || 0}
                    </p>
                  </div>

                  <div className="hidden min-w-[170px] max-w-[220px] lg:block">
                    <p className="text-xs text-gray-400">Teacher</p>
                    <p
                      className="mt-0.5 truncate text-sm font-medium text-gray-700"
                      title={teacherDisplay}
                    >
                      {teacherDisplay}
                    </p>
                  </div>

                  <div className="hidden min-w-[95px] md:block">
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${getStatusColor(cls.status)}`}
                    >
                      <StatusIcon className="mr-1 h-3.5 w-3.5" />
                      {getStatusLabel(cls.status)}
                    </span>
                  </div>

                  {renderActionMenu(cls, isDeleting)}
                </div>

                <div className="mt-3 flex items-center gap-2 border-t border-gray-100 pt-3 sm:hidden">
                  <span className="text-xs text-gray-500">
                    {cls.student_count || 0} students
                  </span>
                  <span className="text-gray-300">•</span>
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${getStatusColor(cls.status)}`}
                  >
                    <StatusIcon className="mr-1 h-3 w-3" />
                    {getStatusLabel(cls.status)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ===== PAGINATION ===== */}
      {hasClasses && totalPages > 1 && (
        <div className="flex flex-wrap justify-center items-center gap-2">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="p-1.5 sm:p-2 border border-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
          >
            <ChevronLeftIcon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
          </button>
          <span className="text-xs sm:text-sm text-gray-600">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages}
            className="p-1.5 sm:p-2 border border-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
          >
            <ChevronRightIcon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
          </button>
        </div>
      )}
    </div>
  );
}